/* eslint-disable jsx-a11y/no-autofocus */
import React, { FC, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { PifApprovalRoleMeta } from "../types";
import "./PifApprovalReview.scss";

interface PifApprovalDecisionPanelProps {
  roleMeta: PifApprovalRoleMeta;
  onSign: () => void;
  onSendBack: (note: string) => void;
  onDecline: (note: string) => void;
}

type PanelMode = "idle" | "sendback" | "decline";

const PifApprovalDecisionPanel: FC<PifApprovalDecisionPanelProps> = ({ roleMeta, onSign, onSendBack, onDecline }) => {
  const [mode, setMode] = useState<PanelMode>("idle");
  const [note, setNote] = useState("");
  const noteValid = note.trim().length > 0;

  const handleCancel = () => {
    setMode("idle");
    setNote("");
  };

  const handleConfirm = () => {
    if (mode === "sendback") onSendBack(note);
    if (mode === "decline") onDecline(note);
    setMode("idle");
    setNote("");
  };

  return (
    <Box className="pif-approval-decision">
      {mode !== "idle" && (
        <Box className="pif-approval-decision__note">
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === "decline" ? "Explain why you're declining this hire" : "What needs to change"}
            inputProps={{ "data-testid": "postif-approval-note" }}
          />
          <Typography className="pif-approval-decision__note-hint">Stored on the audit trail and visible to everyone in the chain.</Typography>
        </Box>
      )}

      <Box className="pif-approval-decision__actions">
        {mode === "idle" ? (
          <>
            <Button variant="text" color="secondary" onClick={() => setMode("decline")} data-testid="postif-approval-decline">
              Decline this hire
            </Button>
            <Button variant="outlined" onClick={() => setMode("sendback")} data-testid="postif-approval-sendback">
              Send back for changes
            </Button>
            <Button variant="contained" onClick={onSign} data-testid="postif-approval-sign">
              {roleMeta.signCta}
            </Button>
          </>
        ) : (
          <>
            <Button variant="text" onClick={handleCancel} data-testid="postif-approval-cancel">
              Cancel
            </Button>
            <Button
              variant="contained"
              color={mode === "decline" ? "secondary" : "primary"}
              disabled={!noteValid}
              onClick={handleConfirm}
              data-testid="postif-approval-confirm"
            >
              {mode === "decline" ? "Decline this hire" : "Send back for changes"}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PifApprovalDecisionPanel;
